import type { GetRequestsListItem, RequestTypeEnum } from "@nlc/shared-types";
import type { ColumnDef } from "@tanstack/react-table";

import { memo, useCallback, useMemo, useState } from "react";
import { Button } from "react-aria-components";

import { Badge } from "@/components/badge";
import { Link } from "@/components/link";
import { DraggableTable as Table } from "@/components/table";
import { getStatusColor } from "@/features/shared/constants/statusColors";
import { useDebounce } from "@/hooks/useDebounce";
import { dateFormatterWithConditionalYear } from "@/utils/dateFormatterWithConditionalYear";
import { renderRequestDesignType } from "@/utils/requestFormatters";

import { useGetTrashedRequests } from "../hooks/useGetTrashedRequests";
import { useRestoreRequests } from "../mutations/useRestoreRequests";

interface TrashedRequestsTableProps {
  search?: string;
}

const NameCell = memo(function NameCell({
  row,
}: {
  row: { original: GetRequestsListItem };
}) {
  return (
    <Link
      className="font-medium text-text-primary hover:no-underline cursor-pointer text-sm leading-tight truncate block"
      href={`/requests/${row.original.id}/edit`}
    >
      {row.original.name}
    </Link>
  );
});

const AccountCell = memo(function AccountCell({
  row,
}: {
  row: { original: GetRequestsListItem };
}) {
  return (
    <div className="text-text-primary font-medium text-sm leading-tight truncate">
      {row.original.account?.name}
    </div>
  );
});

const AssigneeCell = memo(function AssigneeCell({
  row,
}: {
  row: { original: GetRequestsListItem };
}) {
  return (
    <div className="text-text-tertiary text-sm leading-tight truncate">
      {row.original.assignee?.name}
    </div>
  );
});

const StatusCell = memo(function StatusCell({
  row,
}: {
  row: { original: GetRequestsListItem };
}) {
  return (
    <Badge isRounded={true} variant={getStatusColor(row.original.status.id)}>
      {row.original.status.name}
    </Badge>
  );
});

const DeleteAtCell = memo(function DeleteAtCell({
  row,
}: {
  row: { original: GetRequestsListItem };
}) {
  return (
    <span className="text-text-tertiary text-sm truncate">
      {dateFormatterWithConditionalYear(row.original.deletedAt)}
    </span>
  );
});

const DeletedByCell = memo(function DeletedByCell({
  row,
}: {
  row: { original: GetRequestsListItem };
}) {
  const deletedBy = row.original.deletedBy;

  if (!deletedBy) {
    return <span className="text-text-tertiary text-sm truncate">-</span>;
  }

  return (
    <div className="flex items-center gap-2 min-w-0">
      {deletedBy.profilePicture && (
        <img
          alt={deletedBy.name}
          className="size-6 rounded-full object-cover flex-shrink-0"
          src={deletedBy.profilePicture}
        />
      )}
      <span className="text-text-primary font-medium text-sm truncate">
        {deletedBy.name}
      </span>
    </div>
  );
});

const RequestTypeCell = memo(function RequestTypeCell({
  row,
}: {
  row: { original: GetRequestsListItem };
}) {
  return (
    <div className="text-text-tertiary text-sm leading-tight truncate">
      {renderRequestDesignType(
        row.original.requestType as RequestTypeEnum,
        row.original.designType,
      )}
    </div>
  );
});

const ActionCell = memo(function ActionCell({
  isPending,
  onRestore,
  row,
}: {
  isPending: boolean;
  onRestore: (id: number) => void;
  row: { original: GetRequestsListItem };
}) {
  return (
    <Button
      className="px-4 py-2 text-sm font-medium text-text-brand-primary bg-bg-primary border border-border-brand rounded-md hover:bg-bg-tertiary disabled:opacity-50 disabled:cursor-not-allowed"
      isDisabled={isPending}
      onPress={() => onRestore(row.original.id)}
    >
      Restore
    </Button>
  );
});

export const TrashedRequestsTable = memo(function TrashedRequestsTable({
  search,
}: TrashedRequestsTableProps) {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const debouncedQuery = useDebounce(search, 500, () => setPage(1));

  const { data, isLoading } = useGetTrashedRequests({
    limit,
    page,
    search: debouncedQuery,
  });

  const restoreMutation = useRestoreRequests();

  const handleRestore = useCallback(
    (id: number) => {
      restoreMutation.mutate(id);
    },
    [restoreMutation],
  );

  const columns: ColumnDef<GetRequestsListItem>[] = useMemo(
    () => [
      {
        accessorKey: "name",
        cell: ({ row }) => <NameCell row={row} />,
        enableSorting: false,
        header: "Name",
        size: 200,
      },
      {
        accessorKey: "account",
        cell: ({ row }) => <AccountCell row={row} />,
        enableSorting: false,
        header: "Account",
        size: 150,
      },
      {
        accessorKey: "assignee",
        cell: ({ row }) => <AssigneeCell row={row} />,
        enableSorting: false,
        header: "Assignee",
        size: 150,
      },
      {
        accessorKey: "status",
        cell: ({ row }) => <StatusCell row={row} />,
        enableSorting: false,
        header: "Status",
        size: 200,
      },
      {
        accessorKey: "deletedAt",
        cell: ({ row }) => <DeleteAtCell row={row} />,
        enableSorting: false,
        header: "Deleted on",
        size: 150,
      },
      {
        accessorKey: "deletedBy",
        cell: ({ row }) => <DeletedByCell row={row} />,
        enableSorting: false,
        header: "Deleted by",
        size: 170,
      },
      {
        accessorKey: "requestType",
        cell: ({ row }) => <RequestTypeCell row={row} />,
        enableSorting: false,
        header: "Request/Design type",
        size: 180,
      },
      {
        accessorKey: "id",
        cell: ({ row }) => (
          <ActionCell
            isPending={restoreMutation.isPending}
            onRestore={handleRestore}
            row={row}
          />
        ),
        enableSorting: false,
        header: "Action",
        size: 120,
      },
    ],
    [handleRestore, restoreMutation.isPending],
  );

  const paginationConfig = useMemo(() => {
    if (!data?.pagination) return;

    return {
      metadata: {
        currentPage: data.pagination.page,
        itemsPerPage: data.pagination.limit,
        totalItems: data.total,
        totalPages: data.pagination.totalPages,
      },
      onItemsPerPageChange: (newLimit: number) => {
        setLimit(newLimit);
        setPage(1);
      },
      onPageChange: (newPage: number) => setPage(newPage),
      pageSizeOptions: [10, 25, 50, 100],
      showPageSizeSelector: true,
    };
  }, [data]);

  const getRowId = useCallback(
    (request: GetRequestsListItem) => String(request.id),
    [],
  );

  if (!isLoading && (!data?.requests || data.requests.length === 0)) {
    return (
      <div className="flex h-[400px] items-center justify-center">
        <p className="text-text-secondary">No deleted requests found</p>
      </div>
    );
  }

  return (
    <div className="rounded-b-xl bg-bg-primary overflow-auto trashed-table">
      <style>{`
        .trashed-table tbody tr {
          height: 60px !important;
        }
        .trashed-table tbody td {
          max-width: 0;
          overflow: hidden;
        }
        .trashed-table tbody td > div,
        .trashed-table tbody td > span,
        .trashed-table tbody td > a {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          display: block;
        }
      `}</style>
      <Table
        className="rounded-b-xl w-full"
        columns={columns}
        data={data?.requests || []}
        getRowId={getRowId}
        isLoading={isLoading}
        pagination={paginationConfig}
      />
    </div>
  );
});
